import React, { useEffect, useState } from "react";
import { ITkFacilitySearchModalProps } from "../../../type/componentsType/TkFacilitySearchModal.d";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import TkDataTable from "../TkDataTable";
import { ITkDataTableHeadePorps } from "../../../type/componentsType/TkDataTable";
import TkRadio from "../../atoms/TkRadio";
import { Alert, Box, Typography } from "@mui/material";
import "./_style.scss";
import TkCheckbox from "../../atoms/TkCheckbox";
import { TkGetCodeList } from "../../../utils/TkGetCode";
import { Prefectures } from "../../../assets/data/Constants";
import TkSearchForm from "../TkSearchForm";
import { TkSearchFormListItem } from "../../../type/componentsType/TkSearchForm";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";

/**
 * TkFacilitySearchModalコンポーネント
 * @component
 * @param {ITkFacilitySearchModalProps} props - コンポーネントのプロパティ
 * @param {boolean} props.open - 表示／非表示。
 * @param {boolean} props.useCheck - ラジオボタン選択形式／チェックボックス選択形式。
 * @returns {React.FC} TkFacilitySearchModalコンポーネント
 */
const TkFacilitySearchModal: React.FC<ITkFacilitySearchModalProps> = (
  props
) => {
  const {
    open,
    listData,
    useCheck = false,
    onChangeSearchData,
    onSave,
    onClose,
  } = props;

  const [bodyList, setBodyList] = useState<any>();
  const [selectItem, setSelectItem] = useState<any>();
  const [error, setError] = useState(false);

  const headList: ITkDataTableHeadePorps[] = [
    {
      headTitle: "選択",
      dontNeedSort: true,
    },
    {
      headTitle: "契約者名",
    },
    {
      headTitle: "施設名",
    },
    {
      headTitle: "施設所在地",
    },
  ];

  const searchList: TkSearchFormListItem[] = [
    {
      label: "共済契約者番号",
      keyword: "contractorNo",
      type: "label",
      onlyNumber: true,
      inputProps: {
        maxLength: 6,
      },
    },
    {
      label: "契約者名",
      keyword: "corpName",
      type: "label",
      inputProps: {
        maxLength: 85,
      },
    },
    {
      label: "施設番号",
      keyword: "facilityNo",
      type: "label",
      onlyNumber: true,
      inputProps: {
        maxLength: 3,
      },
    },
    {
      label: "施設名",
      keyword: "facilityName",
      type: "label",
      inputProps: {
        maxLength: 85,
      },
    },
    {
      label: "施設所在地（都道府県）",
      keyword: "prefectureCd",
      type: "selectFrom",
      selectPropsList: TkGetCodeList(Prefectures),
    },
  ];

  const schema = {
    contractorNo: {
      pattern: {
        value: /^(\d{6})?$/,
        message: TkPropertyMessageGet("TKMSG_SCR0077E", "6"),
      },
    },
    corpName: {
      pattern: {
        // eslint-disable-next-line no-control-regex
        value: /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F -~｡-ﾟ]+$/,
        message: TkPropertyMessageGet("TKMSG_SCR0061E"),
      },
    },
    facilityNo: {
      pattern: {
        value: /^(\d{3})?$/,
        message: TkPropertyMessageGet("TKMSG_SCR0077E", "3"),
      },
    },
    facilityName: {
      pattern: {
        // eslint-disable-next-line no-control-regex
        value: /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F -~｡-ﾟ]+$/,
        message: TkPropertyMessageGet("TKMSG_SCR0061E"),
      },
    },
  };

  const handelItemSelect = (e: any) => {
    if (useCheck) {
      const res = e;
      setSelectItem((prevSelectItem: any) => {
        if (prevSelectItem) {
          return prevSelectItem.includes(res)
            ? prevSelectItem.filter((item: any) => item !== res)
            : [...prevSelectItem, res];
        } else {
          return [res];
        }
      });
      return;
    }

    setSelectItem(e);
  };

  const handleSearch = (data: any) => {
    if (Object.values(data).every((item) => !item)) {
      setError(true);
      return;
    }

    setError(false);
    onChangeSearchData && onChangeSearchData(data);
  };

  const handleOnSave = () => {
    setError(false);
    onSave && onSave(selectItem);
  };

  const handleOnClose = () => {
    setError(false);
    onClose && onClose();
  };

  useEffect(() => {
    const renderList = listData.map((item: any) => {
      const {
        contractorNumber,
        contractorName,
        companyAddress,
        facilityNumber,
        facilityName,
      } = item;

      const list = [
        {
          children: useCheck ? (
            <TkCheckbox
              checked={selectItem && selectItem.includes(item) ? true : false}
              onClick={() => handelItemSelect(item)}
            />
          ) : (
            <TkRadio
              checked={
                selectItem &&
                selectItem?.contractorNumber === contractorNumber &&
                selectItem?.facilityNumber === facilityNumber
                  ? true
                  : false
              }
              onClick={() => handelItemSelect(item)}
            />
          ),
          width: 40,
        },
        {
          children: (
            <Typography>
              {contractorNumber}:{contractorName}
            </Typography>
          ),
          forSort: contractorNumber,
          width: 320,
        },
        {
          children: (
            <Typography>
              {facilityNumber} : {facilityName}
            </Typography>
          ),
          forSort: facilityNumber,
          width: 320,
        },
        {
          children: <Typography>{companyAddress}</Typography>,
          forSort: companyAddress,
          width: 500,
        },
      ];

      return list;
    });

    setBodyList([...renderList]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData, selectItem]);

  return (
    <TkModalWithTitle
      title="施設検索"
      saveData={selectItem}
      clearSaveData={() => setSelectItem(null)}
      open={open}
      saveButtonDisabled={!selectItem?.length}
      onSave={handleOnSave}
      onClose={handleOnClose}
      className={"TkFacilitySearchModalScroll"}
    >
      <>
        {error && (
          <Alert severity="error">
            {TkPropertyMessageGet("TKMSG_SVR0067E")}
          </Alert>
        )}
        <Box className={"TkFacilitySearchModalSec"}>
          <TkSearchForm
            listData={searchList}
            ruleSchema={schema}
            onSearch={handleSearch}
          />
        </Box>
        <TkDataTable useScroll bodyList={bodyList} headList={headList} />
      </>
    </TkModalWithTitle>
  );
};

export default TkFacilitySearchModal;
